import { useState, useEffect } from 'react'
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { Package, Calendar, TrendingUp, Users, Clipboard, CheckCircle2 } from 'lucide-react'
import { generateProductivityData, CROP_CONFIG } from '../../utils/mockDataGenerator'
import './ProductivityDashboard.css'

function ProductivityDashboard({ crop }) {
    const [productivityData, setProductivityData] = useState(null)
    const [activeView, setActiveView] = useState('overview')

    useEffect(() => {
        const data = generateProductivityData(crop, 30)
        setProductivityData(data)
    }, [crop])

    if (!productivityData) return <div className="loading">Cargando datos de producción...</div>

    const { harvests, labors, metrics, qualityBreakdown } = productivityData
    const cropConfig = CROP_CONFIG[crop] || CROP_CONFIG['Tomate']

    const COLORS = ['#10B981', '#0EA5E9', '#F59E0B', '#EF4444']

    // Prepare weekly production chart data
    const weeklyData = []
    const harvestsByWeek = {}

    harvests.forEach(h => {
        const date = new Date(h.date)
        const weekStart = new Date(date)
        weekStart.setDate(date.getDate() - date.getDay())
        const weekKey = weekStart.toISOString().split('T')[0]

        if (!harvestsByWeek[weekKey]) {
            harvestsByWeek[weekKey] = { total: 0 }
        }
        harvestsByWeek[weekKey].total += h.quantity
    })

    Object.entries(harvestsByWeek).forEach(([week, data], index) => {
        const weekDate = new Date(week)
        weeklyData.push({
            week: `Sem ${index + 1}`,
            dateLabel: weekDate.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }),
            produccion: Math.round(data.total),
            meta: Math.round(metrics.avgPerDay * 7)
        })
    })

    // Prepare quality pie chart data
    const qualityData = [
        { name: 'Premium', value: qualityBreakdown.Premium || 0 },
        { name: 'Primera', value: qualityBreakdown.Primera || 0 },
        { name: 'Segunda', value: qualityBreakdown.Segunda || 0 },
        { name: 'Tercera', value: qualityBreakdown.Tercera || 0 }
    ].filter(d => d.value > 0)

    // Group labors by type
    const laborsByType = {}
    labors.forEach(l => {
        if (!laborsByType[l.type]) {
            laborsByType[l.type] = { tipo: l.type, horas: 0, count: 0 }
        }
        laborsByType[l.type].horas += l.hours
        laborsByType[l.type].count++
    })
    const laborChartData = Object.values(laborsByType)

    return (
        <div className="productivity-dashboard">
            <div className="dashboard-header">
                <div>
                    <h2>📦 Control Productivo - {crop}</h2>
                    <p>Métricas de rendimiento y gestión de labores (últimos 30 días)</p>
                </div>
                <div className="view-buttons">
                    <button
                        className={`view-btn ${activeView === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveView('overview')}
                    >
                        Resumen
                    </button>
                    <button
                        className={`view-btn ${activeView === 'harvests' ? 'active' : ''}`}
                        onClick={() => setActiveView('harvests')}
                    >
                        Cosechas
                    </button>
                    <button
                        className={`view-btn ${activeView === 'labors' ? 'active' : ''}`}
                        onClick={() => setActiveView('labors')}
                    >
                        Labores
                    </button>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="metrics-grid">
                <div className="metric-card glass-card">
                    <Package size={24} />
                    <div className="metric-info">
                        <span className="metric-value">{metrics.kgPerPlant} kg</span>
                        <span className="metric-label">Por Planta</span>
                    </div>
                </div>
                <div className="metric-card glass-card">
                    <TrendingUp size={24} />
                    <div className="metric-info">
                        <span className="metric-value">{metrics.kgPerM2} kg</span>
                        <span className="metric-label">Por m²</span>
                    </div>
                </div>
                <div className="metric-card glass-card">
                    <TrendingUp size={24} />
                    <div className="metric-info">
                        <span className="metric-value">{metrics.tonPerHa} ton</span>
                        <span className="metric-label">Por Hectárea</span>
                    </div>
                    <span className="metric-badge">
                        {metrics.tonPerHa >= cropConfig.finance.yieldTonHa ? '✓ Meta' : '↑ Mejorar'}
                    </span>
                </div>
                <div className="metric-card glass-card highlight">
                    <Package size={24} />
                    <div className="metric-info">
                        <span className="metric-value">{metrics.totalHarvested.toLocaleString()} kg</span>
                        <span className="metric-label">Total Cosechado</span>
                    </div>
                </div>
            </div>

            {activeView === 'overview' && (
                <>
                    {/* Charts Row */}
                    <div className="charts-row">
                        <div className="chart-card glass-card">
                            <h3>📈 Producción Semanal</h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={weeklyData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                    <XAxis dataKey="week" stroke="#64748B" fontSize={12} />
                                    <YAxis stroke="#64748B" fontSize={12} />
                                    <Tooltip
                                        contentStyle={{ background: '#1E293B', border: '1px solid #334155', borderRadius: '8px' }}
                                        formatter={(value) => [`${value} kg`, '']}
                                    />
                                    <Legend />
                                    <Bar dataKey="produccion" fill="#10B981" name="Producción (kg)" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="meta" fill="#334155" name="Meta" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="chart-card glass-card">
                            <h3>📊 Calidad del Producto</h3>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={qualityData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={50}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {qualityData.map((entry, index) => (
                                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Labor Section */}
                    <div className="labor-section">
                        <div className="labor-table glass-card">
                            <h3><Clipboard size={20} /> Labores Recientes</h3>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Labor</th>
                                        <th>Zona</th>
                                        <th>Trabajadores</th>
                                        <th>Horas</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {labors.slice(0, 6).map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.dateDisplay}</td>
                                            <td><span className={`labor-badge ${item.type.toLowerCase()}`}>{item.type}</span></td>
                                            <td>{item.zone}</td>
                                            <td>{item.workers}</td>
                                            <td>{item.hours}h</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="labor-hours glass-card">
                            <h3><Users size={20} /> Horas por Tipo de Labor</h3>
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={laborChartData} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                    <XAxis type="number" stroke="#64748B" fontSize={12} />
                                    <YAxis dataKey="tipo" type="category" stroke="#64748B" fontSize={11} width={90} />
                                    <Tooltip
                                        contentStyle={{ background: '#1E293B', border: '1px solid #334155', borderRadius: '8px' }}
                                        formatter={(value) => [`${value.toFixed(1)}h`, 'Horas']}
                                    />
                                    <Bar dataKey="horas" fill="#0EA5E9" name="Horas" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </>
            )}

            {activeView === 'harvests' && (
                <div className="harvests-section glass-card">
                    <h3><CheckCircle2 size={20} /> Historial de Cosechas</h3>
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Zona</th>
                                    <th>Cantidad</th>
                                    <th>Calidad</th>
                                    {crop === 'Tomate' && <th>°Brix</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {harvests.map((h) => (
                                    <tr key={h.id}>
                                        <td>{h.dateDisplay}</td>
                                        <td>{h.zone}</td>
                                        <td className="highlight">{h.quantity.toFixed(1)} kg</td>
                                        <td><span className={`quality-badge ${h.quality.toLowerCase()}`}>{h.quality}</span></td>
                                        {crop === 'Tomate' && <td>{h.brix || '-'}</td>}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeView === 'labors' && (
                <div className="labors-full-section glass-card">
                    <h3><Clipboard size={20} /> Historial Completo de Labores</h3>
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Tipo</th>
                                    <th>Zona</th>
                                    <th>Trabajadores</th>
                                    <th>Horas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {labors.map((l) => (
                                    <tr key={l.id}>
                                        <td>{l.dateDisplay}</td>
                                        <td><span className={`labor-badge ${l.type.toLowerCase()}`}>{l.type}</span></td>
                                        <td>{l.zone}</td>
                                        <td>{l.workers} personas</td>
                                        <td>{l.hours}h</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ProductivityDashboard
